// src/client/renderer/vfx/items/gravity-well.ts
//
// Plan 17, Task 3.1 — Gravity Well pool.
//
// Triggered by THREE events:
//   1. ItemCastEvent (itemId='gravity_well') → phase='cast':
//        Circular range pulse and vortex centred on the caster.
//        Colour: 0x9966ff (purple). Max-active = 2.
//
//   2. ItemPullEvent (itemId='gravity_well') → phase='active':
//        One short pull-streak per victim (line from victim's pre-pull position
//        toward caster, 250ms). Colour: purple (CONE_COLOUR shared). Max-active = 8.
//
//   3. ItemStealKeysEvent (itemId='gravity_well') → phase='active' + outcome='gold':
//        Key-flow streak (small gold sprites from victim to caster, 400ms).
//        Colour: 0xffd24a (gold). Max-active = 8.
//        Discriminator: args.outcome === 'gold' → key-streak; otherwise → pull-streak.
//
// GSAP: use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Pull and key-steal streaks
//   are intentionally silent follow-up emitters.

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type {
  GameEvent,
  ItemCastEvent,
  ItemPullEvent,
  ItemStealKeysEvent,
} from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Max simultaneously active cast emitters. */
const MAX_CONE_ACTIVE = 2;

/** Max simultaneously active pull-streak emitters. */
const MAX_PULL_ACTIVE = 8;

/** Max simultaneously active key-streak emitters. */
const MAX_KEY_ACTIVE = 8;

/** Gravity well colour — purple. */
const CONE_COLOUR = 0x9966ff;

/** Key-streak colour — gold. */
const KEY_COLOUR = 0xffd24a;

/** Pull-streak lifetime in seconds. */
const PULL_STREAK_LIFETIME = 0.75;

/** Key-streak lifetime in seconds. */
const KEY_STREAK_LIFETIME = 1.0;

/** Cast emitter lifetime in seconds. */
const CONE_LIFETIME = 1.8;

/** World-space radius shown by the range pulse. */
const GRAVITY_WELL_VISUAL_RADIUS = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Emitter builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a radial gravity well visual: a 4-tile range ring plus inward vortex rings.
 */
function buildGravityWellEmitter(
  centerPos: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();
  group.position.set(centerPos.x, centerPos.y + 0.04, centerPos.z);

  const rangeMaterial = new THREE.MeshBasicMaterial({
    color: CONE_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const rangeRing = new THREE.Mesh(
    new THREE.RingGeometry(GRAVITY_WELL_VISUAL_RADIUS * 0.92, GRAVITY_WELL_VISUAL_RADIUS, 96),
    rangeMaterial,
  );
  rangeRing.rotation.x = -Math.PI / 2;
  group.add(rangeRing);

  const vortexMaterial = new THREE.MeshBasicMaterial({
    color: CONE_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const vortexRings: THREE.Mesh[] = [];
  for (let i = 0; i < 4; i += 1) {
    const radius = 0.55 + i * 0.55;
    const ring = new THREE.Mesh(new THREE.RingGeometry(radius * 0.82, radius, 64), vortexMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.rotation.z = i * 0.7;
    ring.position.y = 0.02 + i * 0.015;
    vortexRings.push(ring);
    group.add(ring);
  }

  const coreMaterial = new THREE.SpriteMaterial({
    color: CONE_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const coreSprite = new THREE.Sprite(coreMaterial);
  coreSprite.position.set(0, 0.28, 0);
  coreSprite.scale.set(1.1, 1.1, 1);
  group.add(coreSprite);

  const distortionMaterial = new THREE.LineBasicMaterial({
    color: 0xb58cff,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const distortionGroup = new THREE.Group();
  for (let i = 0; i < 20; i += 1) {
    const angle = (i / 20) * Math.PI * 2;
    const bend = angle + 0.38;
    const outerRadius = GRAVITY_WELL_VISUAL_RADIUS * (0.82 + (i % 3) * 0.07);
    const innerRadius = 0.58 + (i % 4) * 0.08;
    const points = [
      new THREE.Vector3(
        Math.cos(angle) * outerRadius,
        0.16 + (i % 2) * 0.05,
        Math.sin(angle) * outerRadius,
      ),
      new THREE.Vector3(
        Math.cos((angle + bend) * 0.5) * 1.8,
        0.28,
        Math.sin((angle + bend) * 0.5) * 1.8,
      ),
      new THREE.Vector3(Math.cos(bend) * innerRadius, 0.38, Math.sin(bend) * innerRadius),
    ];
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points),
      distortionMaterial,
    );
    distortionGroup.add(line);
  }
  group.add(distortionGroup);

  const darkMaterial = new THREE.MeshBasicMaterial({
    color: 0x10051f,
    transparent: true,
    opacity: 0.0,
    blending: THREE.NormalBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const darkDisc = new THREE.Mesh(new THREE.CircleGeometry(1.25, 64), darkMaterial);
  darkDisc.rotation.x = -Math.PI / 2;
  darkDisc.position.y = 0.03;
  group.add(darkDisc);

  const handle = registry.create('gravity_well_cone');
  state.handle = handle;
  const tl = handle.timeline;

  const rangeProxy = { opacity: 0, scale: 0.74, rotation: 0 };
  tl.to(
    rangeProxy,
    {
      opacity: 0.42,
      scale: 1,
      rotation: Math.PI * 0.25,
      duration: CONE_LIFETIME * 0.35,
      ease: 'power2.out',
      onUpdate: () => {
        rangeMaterial.opacity = rangeProxy.opacity;
        rangeRing.scale.setScalar(rangeProxy.scale);
        rangeRing.rotation.z = rangeProxy.rotation;
      },
    },
    0,
  );

  tl.to(
    rangeProxy,
    {
      opacity: 0,
      scale: 1.08,
      rotation: Math.PI * 0.65,
      duration: CONE_LIFETIME * 0.5,
      ease: 'power1.in',
      onUpdate: () => {
        rangeMaterial.opacity = rangeProxy.opacity;
        rangeRing.scale.setScalar(rangeProxy.scale);
        rangeRing.rotation.z = rangeProxy.rotation;
      },
    },
    CONE_LIFETIME * 0.4,
  );

  const vortexProxy = { opacity: 0, scale: 1.65, rotation: 0 };
  tl.to(
    vortexProxy,
    {
      opacity: 0.62,
      scale: 0.35,
      rotation: Math.PI * 1.8,
      duration: CONE_LIFETIME * 0.62,
      ease: 'power2.out',
      onUpdate: () => {
        vortexMaterial.opacity = vortexProxy.opacity;
        for (let i = 0; i < vortexRings.length; i += 1) {
          const ring = vortexRings[i]!;
          ring.scale.setScalar(vortexProxy.scale + i * 0.12);
          ring.rotation.z = vortexProxy.rotation + i * 0.8;
        }
      },
    },
    0,
  );
  tl.to(
    vortexProxy,
    {
      opacity: 0,
      scale: 0.18,
      rotation: Math.PI * 2.3,
      duration: CONE_LIFETIME * 0.32,
      ease: 'power2.in',
      onUpdate: () => {
        vortexMaterial.opacity = vortexProxy.opacity;
        for (let i = 0; i < vortexRings.length; i += 1) {
          const ring = vortexRings[i]!;
          ring.scale.setScalar(vortexProxy.scale + i * 0.08);
          ring.rotation.z = vortexProxy.rotation + i * 0.8;
        }
      },
    },
    CONE_LIFETIME * 0.62,
  );

  const distortionProxy = { opacity: 0, scale: 1.15, rotation: 0 };
  tl.to(
    distortionProxy,
    {
      opacity: 0.78,
      scale: 0.22,
      rotation: Math.PI * 2.2,
      duration: CONE_LIFETIME * 0.72,
      ease: 'power3.in',
      onUpdate: () => {
        distortionMaterial.opacity = distortionProxy.opacity;
        distortionGroup.scale.setScalar(distortionProxy.scale);
        distortionGroup.rotation.y = distortionProxy.rotation;
      },
    },
    0.12,
  );
  tl.to(
    distortionProxy,
    {
      opacity: 0,
      scale: 0.04,
      rotation: Math.PI * 2.8,
      duration: CONE_LIFETIME * 0.24,
      ease: 'power2.in',
      onUpdate: () => {
        distortionMaterial.opacity = distortionProxy.opacity;
        distortionGroup.scale.setScalar(distortionProxy.scale);
        distortionGroup.rotation.y = distortionProxy.rotation;
      },
    },
    CONE_LIFETIME * 0.72,
  );

  const darkProxy = { opacity: 0, scale: 0.35 };
  tl.to(
    darkProxy,
    {
      opacity: 0.42,
      scale: 1.2,
      duration: CONE_LIFETIME * 0.45,
      ease: 'power2.out',
      onUpdate: () => {
        darkMaterial.opacity = darkProxy.opacity;
        darkDisc.scale.setScalar(darkProxy.scale);
      },
    },
    0.18,
  );
  tl.to(
    darkProxy,
    {
      opacity: 0,
      scale: 0.12,
      duration: CONE_LIFETIME * 0.38,
      ease: 'power3.in',
      onUpdate: () => {
        darkMaterial.opacity = darkProxy.opacity;
        darkDisc.scale.setScalar(darkProxy.scale);
      },
    },
    CONE_LIFETIME * 0.58,
  );

  const coreProxy = { opacity: 0, scale: 0.5 };
  tl.to(
    coreProxy,
    {
      opacity: 0.74,
      scale: 1.35,
      duration: CONE_LIFETIME * 0.35,
      ease: 'power2.out',
      onUpdate: () => {
        coreMaterial.opacity = coreProxy.opacity;
        coreSprite.scale.set(coreProxy.scale, coreProxy.scale, 1);
      },
    },
    0.08,
  );
  tl.to(
    coreProxy,
    {
      opacity: 0,
      scale: 0.25,
      duration: CONE_LIFETIME * 0.45,
      ease: 'power2.in',
      onUpdate: () => {
        coreMaterial.opacity = coreProxy.opacity;
        coreSprite.scale.set(coreProxy.scale, coreProxy.scale, 1);
      },
    },
    CONE_LIFETIME * 0.42,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CONE_LIFETIME,
  );

  return group;
}

/**
 * Build a pull-streak emitter: a short line from victim toward caster (250ms).
 */
function buildPullStreakEmitter(
  fromPos: { x: number; y: number; z: number },
  toPos: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Line from victim (fromPos) toward caster (toPos)
  const lineMaterial = new THREE.LineBasicMaterial({
    color: CONE_COLOUR,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const fromVec = new THREE.Vector3(fromPos.x, fromPos.y, fromPos.z);
  const toVec = new THREE.Vector3(toPos.x, toPos.y, toPos.z);
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([fromVec, toVec]);
  const line = new THREE.Line(lineGeometry, lineMaterial);
  group.add(line);

  const handle = registry.create('gravity_well_pull_streak');
  state.handle = handle;
  const tl = handle.timeline;

  // Fade out over 250ms
  const opProxy = { value: 0.8 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: PULL_STREAK_LIFETIME,
      ease: 'power2.in',
      onUpdate: () => {
        lineMaterial.opacity = opProxy.value;
      },
    },
    0,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    PULL_STREAK_LIFETIME,
  );

  return group;
}

/**
 * Build a key-streak emitter: gold sprites flowing from victim to caster (400ms).
 */
function buildKeyStreakEmitter(
  fromPos: { x: number; y: number; z: number },
  toPos: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Key sprite: a gold sprite moving from victim to caster
  const keyMaterial = new THREE.SpriteMaterial({
    color: KEY_COLOUR,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const keySprite = new THREE.Sprite(keyMaterial);
  keySprite.position.set(fromPos.x, fromPos.y, fromPos.z);
  keySprite.scale.set(0.3, 0.3, 1);
  group.add(keySprite);

  const handle = registry.create('gravity_well_key_streak');
  state.handle = handle;
  const tl = handle.timeline;

  // Sprite travels from victim to caster
  tl.to(
    keySprite.position,
    {
      x: toPos.x,
      y: toPos.y,
      z: toPos.z,
      duration: KEY_STREAK_LIFETIME,
      ease: 'power2.in',
    },
    0,
  );

  // Fade out toward end
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: KEY_STREAK_LIFETIME * 0.4,
      ease: 'power1.in',
      onUpdate: () => {
        keyMaterial.opacity = opProxy.value;
      },
    },
    KEY_STREAK_LIFETIME * 0.6,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    KEY_STREAK_LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createGravityWellPool
// ─────────────────────────────────────────────────────────────────────────────

export function createGravityWellPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /** Cone emitters: one-shot per cast. Max-active = 2. */
  const coneStates = new Map<THREE.Object3D, EmitterState>();

  /** Pull-streak emitters: one-shot per victim. Max-active = 8. */
  const pullStates = new Map<THREE.Object3D, EmitterState>();

  /** Key-streak emitters: one-shot per steal-keys event. Max-active = 8. */
  const keyStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;

    if (phase === 'cast') {
      // ── Cone phase ───────────────────────────────────────────────────────
      if (coneStates.size >= MAX_CONE_ACTIVE) return null;

      const casterPos = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildGravityWellEmitter(casterPos, deps, state);
      scene.add(group);
      coneStates.set(group, state);
      return group;
    } else if (phase === 'active') {
      // ── Active phase: pull-streak vs key-streak via outcome discriminator ─
      const isKeyStreak = args.outcome === 'gold';

      if (isKeyStreak) {
        if (keyStates.size >= MAX_KEY_ACTIVE) return null;

        const fromPos = args.targetPosition ?? { x: 0, y: 0, z: 0 };
        const toPos = args.casterPosition ?? { x: fromPos.x + 1, y: fromPos.y, z: fromPos.z };

        const state: EmitterState = { handle: null!, finished: false };
        const group = buildKeyStreakEmitter(fromPos, toPos, deps, state);
        scene.add(group);
        keyStates.set(group, state);
        return group;
      } else {
        // pull-streak (no outcome / non-gold)
        if (pullStates.size >= MAX_PULL_ACTIVE) return null;

        const fromPos = args.targetPosition ?? { x: 0, y: 0, z: 0 };
        const toPos = args.casterPosition ?? { x: fromPos.x + 1, y: fromPos.y, z: fromPos.z };

        const state: EmitterState = { handle: null!, finished: false };
        const group = buildPullStreakEmitter(fromPos, toPos, deps, state);
        scene.add(group);
        pullStates.set(group, state);
        return group;
      }
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const emitter of coneStates.keys()) all.add(emitter);
    for (const emitter of pullStates.keys()) all.add(emitter);
    for (const emitter of keyStates.keys()) all.add(emitter);
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const coneState = coneStates.get(emitter);
    if (coneState) return coneState.finished;
    const pullState = pullStates.get(emitter);
    if (pullState) return pullState.finished;
    const keyState = keyStates.get(emitter);
    if (keyState) return keyState.finished;
    return true; // unknown emitter
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const coneState = coneStates.get(emitter);
    if (coneState) {
      coneState.handle.dispose();
      coneStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      return;
    }
    const pullState = pullStates.get(emitter);
    if (pullState) {
      pullState.handle.dispose();
      pullStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      return;
    }
    const keyState = keyStates.get(emitter);
    if (keyState) {
      keyState.handle.dispose();
      keyStates.delete(emitter);
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of coneStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    coneStates.clear();

    for (const [emitter, state] of pullStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    pullStates.clear();

    for (const [emitter, state] of keyStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    keyStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveGravityWellForEvent
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves Gravity Well VFX events.
 *
 * Discriminator: for ItemPullEvent → phase='active' (pull-streak, no outcome).
 *                for ItemStealKeysEvent → phase='active', outcome='gold' (key-streak).
 * Pool checks args.outcome === 'gold' to distinguish the two active-phase visuals.
 *
 * Optional position callback:
 *   getPositionForPlayer?(playerId) resolves world positions for caster and victim.
 *   If omitted, positions are undefined (pool uses fallback { 0,0,0 }).
 *   Task 6.1 wires real positions.
 */
export function resolveGravityWellForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'gravity_well'; args: ItemVfxSpawnArgs } | null {
  if (event.type === 'item_cast') {
    if (event.itemId !== 'gravity_well') return null;
    const cast = event as ItemCastEvent;
    const casterPos =
      cast.casterId && getPositionForPlayer ? getPositionForPlayer(cast.casterId) : undefined;
    const targetPos =
      cast.targetId && getPositionForPlayer ? getPositionForPlayer(cast.targetId) : undefined;
    return {
      poolId: 'gravity_well',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: casterPos,
        targetPosition: targetPos,
      },
    };
  }

  if (event.type === 'item_pull') {
    const pull = event as ItemPullEvent;
    if (pull.itemId !== 'gravity_well') return null;
    const casterPos =
      pull.casterId && getPositionForPlayer ? getPositionForPlayer(pull.casterId) : undefined;
    const victimPos =
      pull.victimId && getPositionForPlayer ? getPositionForPlayer(pull.victimId) : undefined;
    return {
      poolId: 'gravity_well',
      args: {
        phase: 'active',
        casterId: pull.casterId,
        casterPosition: casterPos,
        targetPosition: victimPos, // victim's pre-pull position
        // No outcome: pull-streak (not key-streak)
      },
    };
  }

  if (event.type === 'item_steal_keys') {
    const steal = event as ItemStealKeysEvent;
    // itemId is 'gravity_well' for now; future steal-items may share this event type
    if (steal.itemId !== 'gravity_well') return null;
    const casterPos =
      steal.casterId && getPositionForPlayer ? getPositionForPlayer(steal.casterId) : undefined;
    const victimPos =
      steal.victimId && getPositionForPlayer ? getPositionForPlayer(steal.victimId) : undefined;
    return {
      poolId: 'gravity_well',
      args: {
        phase: 'active',
        casterId: steal.casterId,
        casterPosition: casterPos,
        targetPosition: victimPos,
        outcome: 'gold', // discriminates key-streak from pull-streak
      },
    };
  }

  return null;
}
